import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { battlesApi } from "../api";
import { UserContext } from "../context/UserContext";
import { useBattleCovers } from "../hooks/useBattleCovers";
import "../css/pages/BattlesPage.css";
import BattleCard from "../components/battles/BattleCard";
import HrnbButton from "../components/controls/HrnbButton";
import HrnbContainer, { HrnbCol, HrnbRow } from "../components/controls/HrnbContainer";
import HrnbAlert from "../components/controls/HrnbAlert";
import HrnbSkeleton, {SkeletonCard, SkeletonText} from "../components/controls/HrnbSkeleton";
import HrnbSearchFilter from "../components/controls/HrnbSearchFilter";
import HrnbSelect from "../components/controls/HrnbSelect";
import { PlusIcon, BackIcon, ForwardIcon } from "../icons";

function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightMatch(text = "", q = "") {
    if (!q) return text;
    const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
    const parts = text.split(re);
    return parts.map((part, i) =>
        re.test(part) ? <mark key={i}>{part}</mark> : <span key={i}>{part}
</span>
    );
}

const STATUS_OPTIONS = [
    { value: "PLANNED", label: "Запланированные" },
    { value: "ACTIVE", label: "Активные" },
    { value: "FINISHED", label: "Завершённые" },
    { value: "CANCELLED", label: "Отменённые" },
];

const TYPE_OPTIONS = [
    { value: "SINGLE", label: "Индивидуальные" },
    { value: "TEAM", label: "Командные" },
];

const FORMAT_OPTIONS = [
    { value: "AUDIO", label: "🎵 Аудио" },
    { value: "TEXT", label: "📝 Текст" },
];

const VISIBILITY_OPTIONS = [
    { value: "PUBLIC", label: "Публичные" },
    { value: "PRIVATE", label: "Частные" },
    { value: "INVITE_ONLY", label: "По приглашению" },
];

export default function BattlesPage() {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [battles, setBattles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Поиск и фильтры
    const [search, setSearch] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterFormat, setFilterFormat] = useState("");
    const [filterVisibility, setFilterVisibility] = useState("");

    // Пагинация
    const [page, setPage] = useState(1);
    const [pageSize] = useState(9);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        let mounted = true;
        const fetchBattles = async () => {
            setLoading(true);
            setError("");
            try {
                const params = {
                    page: page - 1,
                    size: pageSize,
                    sortBy: "lastUpdated",
                    direction: "DESC",
                    ...(filterStatus && { status: filterStatus }),
                    ...(filterType && { type: filterType }),
                    ...(filterFormat && { format: filterFormat }),
                    // visibility фильтруется на клиенте (бэкенд не поддерживает)
                    ...(searchQuery && { search: searchQuery }),
                };
                // Для неавторизованных пользователей используем публичный API
                const data = currentUser ? await battlesApi.list(params) : await battlesApi.listPublic(params);
                const battlesData = data?.content || data?.data || data || [];
                const totalItems = data?.totalElements || data?.total || battlesData.length;

                if (mounted) {
                    setBattles(Array.isArray(battlesData) ? battlesData : []);
                    setTotal(Number(totalItems));
                }
            } catch (err) {
                console.error("Ошибка загрузки баттлов:", err);
                if (mounted) setError("Ошибка загрузки баттлов");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchBattles();
        return () => {
            mounted = false;
        };
    }, [page, pageSize, filterStatus, filterType, filterFormat, searchQuery, currentUser]);

    // Сброс на страницу 1 при изменении фильтров (которые отправляются на сервер)
    useEffect(() => {
        setPage(1);
    }, [filterStatus, filterType, filterFormat, searchQuery]);

    // Клиентская фильтрация (т.к. бэкенд не поддерживает visibility)
    const filteredBattles = useMemo(() => {
        let result = battles;
        if (filterVisibility) {
            result = result.filter(b => b.visibility === filterVisibility);
        }
        return result;
    }, [battles, filterVisibility]);

    // Загрузка обложек для баттлов
    const { coverUrls, loading: coversLoading } = useBattleCovers(filteredBattles);

    // Подсветка совпадений в названии (поиск выполняется на сервере)
    const highlightedBattles = useMemo(() => {
        return filteredBattles.map((b) => ({
            ...b,
            highlightedTitle: highlightMatch(b.title || "", searchQuery),
        }));
    }, [filteredBattles, searchQuery]);

    const totalPages = Math.ceil(total / pageSize);

    const handleClearFilters = () => {
        setSearch("");
        setSearchQuery("");
        setFilterStatus("");
        setFilterType("");
        setFilterFormat("");
        setFilterVisibility("");
        setPage(1);
    };

    if (loading) {
        return (
            <HrnbContainer className="battles-page">
                <HrnbRow style={{ marginBottom: 16, justifyContent: 'space-between' }}>
                    <SkeletonText lines={1} width="120px" />
                    <SkeletonText lines={1} width="140px" />
                </HrnbRow>
                <HrnbRow style={{ marginBottom: 16 }}>
                    <HrnbCol col={12}>
                        <HrnbSkeleton height="48px" />
                    </HrnbCol>
                </HrnbRow>
                <HrnbRow style={{ marginBottom: 16 }}>
                    <HrnbCol col={12} style={{ maxWidth: 300 }}>
                        <HrnbSkeleton height="40px" />
                    </HrnbCol>
                </HrnbRow>
                <HrnbRow className="battles-grid">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <HrnbCol col={4} xl={3} key={i}>
                            <SkeletonCard />
                        </HrnbCol>
                    ))}
                </HrnbRow>
            </HrnbContainer>
        );
    }

    if (error) {
        return (
            <HrnbContainer>
                <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                    <HrnbAlert type="danger">{error}</HrnbAlert>
                </HrnbRow>
            </HrnbContainer>
        );
    }

    return (
        <HrnbContainer className="battles-page">
            <HrnbRow className="hrnb-row--center hrnb-row--align-center" style={{ marginBottom: 16, justifyContent: 'space-between' }}>
                <h2>Битвы</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ color: "#b0b6c3" }}>
                        Показано <strong>{filteredBattles.length}</strong> из <strong>{total}</strong>
                    </div>
                    {currentUser && (
                        <HrnbButton variant="primary" onClick={() => navigate('/battles/create')} icon={<PlusIcon/>}>
                            Создать баттл
                        </HrnbButton>
                    )}
                </div>
            </HrnbRow>

            <HrnbRow className="hrnb-row--center hrnb-row--align-center" style={{ marginBottom: 16 }}>
                <HrnbCol col={12}>
                    <HrnbSearchFilter
                        search={search}
                        onSearchChange={setSearch}
                        onSearchSubmit={setSearchQuery}
                        filterValue={filterStatus}
                        onFilterChange={setFilterStatus}
                        filterOptions={STATUS_OPTIONS}
                        onClear={handleClearFilters}
                        debounceMs={300}
                    />
                </HrnbCol>
            </HrnbRow>

            {/* Дополнительные фильтры */}
            <HrnbRow className="hrnb-row--center hrnb-row--align-center battles-filters-row" style={{ marginBottom: 16, gap: 16 }}>
                <HrnbCol col={12} sm={6} md={3}>
                    <HrnbSelect
                        value={filterType}
                        onChange={setFilterType}
                        options={[{ value: "", label: "Все типы" }, ...TYPE_OPTIONS]}
                        placeholder="Тип баттла"
                    />
                </HrnbCol>
                <HrnbCol col={12} sm={6} md={3}>
                    <HrnbSelect
                        value={filterFormat}
                        onChange={setFilterFormat}
                        options={[{ value: "", label: "Все форматы" }, ...FORMAT_OPTIONS]}
                        placeholder="Формат"
                    />
                </HrnbCol>
                <HrnbCol col={12} sm={6} md={3}>
                    <HrnbSelect
                        value={filterVisibility}
                        onChange={setFilterVisibility}
                        options={[{ value: "", label: "Все" }, ...VISIBILITY_OPTIONS]}
                        placeholder="Видимость"
                    />
                </HrnbCol>
            </HrnbRow>

            {/* Список баттлов */}
            {filteredBattles.length === 0 ? (
                <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                    <div>Баттлы не найдены</div>
                </HrnbRow>
            ) : (
                <>
                    <HrnbRow className="hrnb-row--center battles-grid">
                        {highlightedBattles.map((battle) => (
                            <HrnbCol col={4} xl={3} key={battle.id}>
                                <BattleCard battle={battle} coverUrl={coverUrls[battle.id]} />
                            </HrnbCol>
                        ))}
                    </HrnbRow>

                    {/* Пагинация */}
                    {totalPages > 1 && (
                        <HrnbRow className="hrnb-row--center hrnb-row--align-center" style={{ marginTop: 24 }}>
                            <HrnbCol col={12}>
                                <div className="battles-pagination">
                                    <HrnbButton
                                        variant="secondary"
                                        disabled={page === 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        icon={<BackIcon />}
                                    >
                                        Назад
                                    </HrnbButton>
                                    <span className="pagination-info">
                                        Страница {page} из {totalPages}
                                    </span>
                                    <HrnbButton
                                        variant="secondary"
                                        disabled={page === totalPages}
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        icon={<ForwardIcon />}
                                    >
                                        Вперёд
                                    </HrnbButton>
                                </div>
                            </HrnbCol>
                        </HrnbRow>
                    )}
                </>
            )}
        </HrnbContainer>
    );
}
